import * as React from "react";
import { getArrow } from "perfect-arrows";

type PerfectArrowProps = {
  a: { x: number; y: number };
  b: { x: number; y: number };
  startArrow?: boolean;
  endArrow?: boolean;
  bow?: number;
  stretch?: number;
  padStart?: number;
  padEnd?: number;
  stretchMin?: number;
  stretchMax?: number;
  flip?: boolean;
  straights?: boolean;
  onClick(): void;
  clickPadding?: number;
};

const PerfectArrow = React.forwardRef<SVGRectElement, PerfectArrowProps>(
  (props, ref) => {
    const [bbox, setBbox] = React.useState<DOMRect | null>(null);
    const {
      a,
      b,
      startArrow,
      onClick,
      endArrow = true,
      bow = 0,
      stretch = 0.5,
      padStart = 10,
      padEnd = 10,
      stretchMin = 0,
      stretchMax = 360,
      flip = false,
      straights = true,
      clickPadding = 25,
    } = props;

    const arrow = getArrow(a.x, a.y, b.x, b.y, {
      bow,
      stretch,
      padStart,
      padEnd,
      stretchMin,
      stretchMax,
      flip,
      straights,
    });

    const [sx, sy, cx, cy, ex, ey, ae, as, ac] = arrow;

    const endAngleAsDegrees = ae * (180 / Math.PI);
    const startAngleAsDegrees = as * (180 / Math.PI);

    const getBbox = React.useCallback((el: SVGPathElement | null) => {
      if (el) {
        setBbox(el.getBBox());
      }
    }, []);

    const strokeColor = "hsl(210, 100%, 55.9%)";

    return (
      <g strokeWidth={3}>
        {bbox && (
          <rect
            fill="transparent"
            stroke="none"
            cursor="pointer"
            role="button"
            onClick={() => {
              if (onClick) {
                onClick(bbox);
              }
            }}
            ref={ref}
            x={bbox.x}
            y={bbox.y}
            width={bbox.width + clickPadding}
            height={bbox.height + clickPadding}
            className="peer"
          />
        )}
        <path
          ref={getBbox}
          d={`M${sx},${sy} Q${cx},${cy} ${ex},${ey}`}
          className="drop-shadow-lg transition-colors peer-hover:stroke-emerald-400  peer-active:stroke-emerald-600"
          fill="none"
          stroke={strokeColor}
        />
        {startArrow ? (
          <polygon
            points="0,-6 12,0, 0,6"
            stroke={strokeColor}
            fill={strokeColor}
            className="drop-shadow-lg transition-colors peer-hover:fill-emerald-400 peer-hover:stroke-emerald-400 peer-active:fill-emerald-600 peer-active:stroke-emerald-600"
            transform={`translate(${sx},${sy}) rotate(${startAngleAsDegrees})`}
          />
        ) : (
          <circle
            cx={sx}
            cy={sy}
            r={4}
            stroke={strokeColor}
            fill="white"
            className="drop-shadow-lg transition-colors peer-hover:stroke-emerald-400 peer-active:stroke-emerald-600"
          />
        )}
        {endArrow && (
          <polygon
            name="end-arrow"
            className="drop-shadow-lg transition-colors peer-hover:fill-emerald-400 peer-hover:stroke-emerald-400 peer-active:fill-emerald-600 peer-active:stroke-emerald-600"
            points="0,-6 12,0, 0,6"
            stroke={strokeColor}
            fill={strokeColor}
            transform={`translate(${ex},${ey}) rotate(${endAngleAsDegrees})`}
          />
        )}
      </g>
    );
  }
);

export default PerfectArrow;
